import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
const MAX_PIN_LENGTH = 6;
export function ManagerPinPad({ onSubmit, error, loading = false, prompt = 'Enter Manager PIN to unlock', submitLabel = 'Unlock', loadingLabel = 'Verifying...', showRemember = true, rememberLabel = 'Remember on this device for 24 hours', }) {
    const [pin, setPin] = useState('');
    const [remember, setRemember] = useState(false);
    const pinRef = useRef('');
    const appendDigit = useCallback((digit) => {
        if (!/^\d$/.test(digit))
            return;
        setPin((current) => {
            const next = current.length < MAX_PIN_LENGTH ? `${current}${digit}` : current;
            pinRef.current = next;
            return next;
        });
    }, []);
    const removeLastDigit = useCallback(() => {
        setPin((current) => {
            const next = current.slice(0, -1);
            pinRef.current = next;
            return next;
        });
    }, []);
    const submitPin = useCallback(() => {
        const currentPin = pinRef.current;
        if (currentPin.length >= 4)
            onSubmit(currentPin, remember);
    }, [onSubmit, remember]);
    const handleKey = useCallback((key) => {
        if (key === '⌫')
            removeLastDigit();
        else
            appendDigit(key);
    }, [appendDigit, removeLastDigit]);
    useEffect(() => {
        const handleKeyboardInput = (event) => {
            if (loading || event.altKey || event.ctrlKey || event.metaKey)
                return;
            // Let other fields on the same screen (e.g. a note box) receive their own typing.
            const target = event.target;
            if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable))
                return;
            if (/^\d$/.test(event.key)) {
                event.preventDefault();
                appendDigit(event.key);
                return;
            }
            if (event.key === 'Backspace' || event.key === 'Delete') {
                event.preventDefault();
                removeLastDigit();
                return;
            }
            if (event.key === 'Enter') {
                event.preventDefault();
                submitPin();
            }
        };
        document.addEventListener('keydown', handleKeyboardInput);
        return () => document.removeEventListener('keydown', handleKeyboardInput);
    }, [appendDigit, loading, removeLastDigit, submitPin]);
    return (_jsxs("div", { className: "flex flex-col items-center gap-4 py-6", children: [_jsx(Lock, { className: "w-10 h-10 text-amber-400" }), _jsx("p", { className: "text-slate-300 text-sm text-center", children: prompt }), _jsxs("div", { className: "flex justify-center gap-2 mb-2", children: [[0, 1, 2, 3].map((i) => (_jsx("div", { className: `w-3 h-3 rounded-full ${i < pin.length ? 'bg-amber-400' : 'bg-slate-600'}` }, i))), pin.length > 4 && [4, 5].map((i) => (_jsx("div", { className: `w-3 h-3 rounded-full ${i < pin.length ? 'bg-amber-400' : 'bg-slate-600'}` }, i)))] }), _jsx("div", { className: "grid grid-cols-3 gap-2 w-56", children: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'].map((key) => (_jsx("button", { type: "button", onClick: () => key && handleKey(key), disabled: !key || loading, className: `h-12 rounded-lg text-lg font-semibold transition-colors ${!key ? 'invisible'
                        : key === '⌫' ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                            : 'bg-slate-800 text-white hover:bg-slate-700'}`, children: key }, key))) }), _jsx(Button, { onClick: submitPin, disabled: pin.length < 4 || loading, className: "w-56 bg-amber-600 hover:bg-amber-500 text-white", children: loading ? loadingLabel : submitLabel }), showRemember && (_jsxs("label", { className: "flex items-center gap-2 text-xs text-slate-400 cursor-pointer select-none", children: [_jsx("input", { type: "checkbox", checked: remember, onChange: (e) => setRemember(e.target.checked), className: "w-3.5 h-3.5 accent-amber-500" }), rememberLabel] })), error && _jsx("p", { className: "text-red-400 text-sm", children: error })] }));
}
